import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  Stack,
  Button,
  Link
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { Close as CloseIcon, Link as LinkIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import type { Tournament } from '../../../../models';
import { CRICKET_XTRACT_PLATFORM_NAMES } from '../../cricketPlatforms';

interface TournamentDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  tournament: Tournament | null;
}

const TournamentDetailsDrawer: React.FC<TournamentDetailsDrawerProps> = ({ open, onClose, tournament }) => {
  if (!tournament) return null;

  const getPlatformName = (platform: string): string => {
    return CRICKET_XTRACT_PLATFORM_NAMES[platform] || platform;
  };

  const getSportColor = (sport: string): 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' => {
    switch (sport.toLowerCase()) {
      case 'cricket':
        return 'primary';
      case 'football':
        return 'success';
      case 'kabaddi':
        return 'warning';
      case 'basketball':
        return 'secondary';
      default:
        return 'info';
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 450 } }
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Tournament Details</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {/* Tournament Basic Info */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Tournament Information
          </Typography>
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Name
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                {tournament.name}
              </Typography>
            </Box>
            
            {tournament.long_name && tournament.long_name !== tournament.name && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Full Name
                </Typography>
                <Typography variant="body1">
                  {tournament.long_name}
                </Typography>
              </Box>
            )}

            {tournament.short_name && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Short Name
                </Typography>
                <Typography variant="body1">
                  {tournament.short_name}
                </Typography>
              </Box>
            )}

            <Box>
              <Typography variant="body2" color="text.secondary">
                Sport
              </Typography>
              <Chip
                label={tournament.sport.charAt(0).toUpperCase() + tournament.sport.slice(1)}
                color={getSportColor(tournament.sport)}
                size="small"
              />
            </Box>

            {tournament.format && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Format
                </Typography>
                <Typography variant="body1">
                  {tournament.format}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>

        {/* Tournament Dates */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Tournament Period
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Start Date
              </Typography>
              <Typography variant="body1">
                {format(new Date(tournament.start_date), 'MMM dd, yyyy')}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                End Date
              </Typography>
              <Typography variant="body1">
                {format(new Date(tournament.end_date), 'MMM dd, yyyy')}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Platform Information */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Platform Information
          </Typography>
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Platform
              </Typography>
              <Typography variant="body1">
                {getPlatformName(tournament.platform)}
              </Typography>
            </Box>

            {tournament.platform_id && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Platform ID
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                  {tournament.platform_id}
                </Typography>
              </Box>
            )}

            {tournament.platform_sub_id && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Platform Sub ID
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                  {tournament.platform_sub_id}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>

        {/* Related IDs */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Related IDs
          </Typography>
          <Stack spacing={1}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Platform ID
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                {tournament.platform_id}
              </Typography>
            </Box>

            {tournament.series_id && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Series ID
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {tournament.series_id}
                  </Typography>
                  <Link
                    href={`/series/${tournament.series_id}`}
                    underline="hover"
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <LinkIcon fontSize="small" />
                  </Link>
                </Box>
              </Box>
            )}

            {tournament.slug && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Slug
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                  {tournament.slug}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>

        {/* Extra Information */}
        {tournament.extra_info && Object.keys(tournament.extra_info).length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Additional Information
            </Typography>
            <Box sx={{ 
              p: 1.5, 
              bgcolor: 'action.hover', 
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.875rem'
            }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {JSON.stringify(tournament.extra_info, null, 2)}
              </pre>
            </Box>
          </Box>
        )}

        {/* Timestamps */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Timestamps
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Created
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '0.875rem' }}>
                {format(new Date(tournament.created_at), 'MMM dd, yyyy HH:mm')}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Updated
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '0.875rem' }}>
                {format(new Date(tournament.updated_at), 'MMM dd, yyyy HH:mm')}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Actions */}
        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => window.location.href = `/tournaments/${tournament.id}`}
            sx={{ mb: 1 }}
          >
            View Tournament Details
          </Button>
          {tournament.series_id && (
            <Button
              variant="outlined"
              fullWidth
              onClick={() => window.location.href = `/series/${tournament.series_id}`}
            >
              View Series Details
            </Button>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default TournamentDetailsDrawer; 